// ── CSC filing documents, as links ───────────────────────────────────────────
// The stamped acknowledgments and attachments CSC holds for a filing, shown
// wherever a filing is — the CSC review queue, the UCC register, the filing's
// detail, and the account's Monitoring tab — so a PM can open the filed copy
// without a CSC login.
//
// Documents come through /api/cscDocument (proxied: the CSC credentials never
// reach the browser, and only documents listed on that order can be fetched).
// CSC's billing-reference sheet is left out of the register views: it is an
// invoice artefact, not part of the filing.
import { B } from "../../../shared/theme";

// The window is opened before the request so the browser does not treat it as
// a pop-up; it is pointed at the PDF once it arrives.
export const openCscDoc = async (api, order, doc) => {
  const w = window.open("", "_blank");
  try {
    const d = await api.get("cscDocument", { order, doc });
    const bytes = Uint8Array.from(atob(d.base64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || "application/pdf" }));
    if (w) w.location.href = url; else window.open(url, "_blank");
  } catch (e) {
    if (w) w.close();
    alert("Could not open the CSC document: " + e.message);
  }
};

const short = d => String(d.description || "Document").replace(/\.pdf$/i, "").replace(/_/g, " ");
const evLabel = t => ({ Initial: "UCC-1", Continuation: "Continuation", Amendment: "Amendment",
                        Assignment: "Assignment", Termination: "Termination" }[t] || t || "");

// Everything filed for one register filing: the stamped copy in Box (the link
// entered with a filing made outside CSC) and CSC's documents.
export const FilingDocs = ({ api, v, compact }) => {
  const box = v && v.box_url;
  const csc = ((v && v.cscdocs) || []).filter(d => !/billing/i.test(d.description || ""));
  if (!box && !csc.length) return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
  return <span style={{ display: "flex", flexDirection: compact ? "column" : "row", flexWrap: "wrap", gap: compact ? 2 : 10 }}>
    {box && <a href={box} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
               style={{ color: B.cyan, fontSize: 12, whiteSpace: "nowrap" }}>📄 Filed copy (Box)</a>}
    {csc.length > 0 && <CscDocLinks api={api} docs={csc} compact={compact} />}
  </span>;
};

// docs: [{ order, id, description, eventtype }] as getUCCFilings returns them.
export const CscDocLinks = ({ api, docs, compact }) => {
  const list = (docs || []).filter(d => !/billing/i.test(d.description || ""));
  if (!list.length) return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
  const multi = new Set(list.map(d => d.eventtype)).size > 1;
  return <span style={{ display: "flex", flexDirection: compact ? "column" : "row", flexWrap: "wrap",
                        gap: compact ? 2 : 10 }}>
    {list.map(d => (
      <a key={d.order + "|" + d.id} href="#" title={d.description || ""}
         onClick={e => { e.preventDefault(); e.stopPropagation(); openCscDoc(api, d.order, d.id); }}
         style={{ color: B.cyan, fontSize: 12, whiteSpace: "nowrap" }}>
        📄 {multi ? evLabel(d.eventtype) + " · " : ""}{short(d)}
      </a>
    ))}
  </span>;
};
